<template>
  <section class="admin-content" id="appointments-page">
    <Navbar />
    <main class="admin-main">

      <!-- Header -->
      <div class="page-header">
        <div class="container">
          <div class="row align-items-center p-t-40 p-b-40">
            <div class="col-md-8">
              <h2 class="page-title"><i class="mdi mdi-calendar-clock mr-2"></i>Appointments</h2>
              <p class="page-subtitle">Manage patient appointments for your facility</p>
            </div>
            <div class="col-md-4 text-right">
              <button type="button" class="btn btn-tele mr-2 mb-2" @click="teleOpen = true">
                <i class="fe fe-video mr-1"></i> Send for Telemedicine
              </button>
              <button class="btn btn-create mb-2" data-toggle="modal" data-target="#createModal">
                <i class="fe fe-plus mr-1"></i> New Appointment
              </button>
              <div>
                <router-link to="/telemedicine" class="text-white small"><i class="fe fe-list mr-1"></i>All telemedicine consultations</router-link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Stats Row -->
      <div class="container m-t-neg">
        <div class="row">
          <div class="col-md-3 col-6" v-for="stat in stats" :key="stat.label">
            <div class="stat-card" :style="{ borderLeftColor: stat.color }">
              <p class="stat-count" :style="{ color: stat.color }">{{ stat.count }}</p>
              <p class="stat-label">{{ stat.label }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- List / Calendar -->
      <div class="container m-t-20">
        <div class="btn-group btn-group-sm mb-2" role="group" aria-label="Appointments view">
          <button type="button" class="btn" :class="view === 'list' ? 'btn-primary' : 'btn-light'" @click="view = 'list'">
            <i class="fe fe-list mr-1"></i>List
          </button>
          <button type="button" class="btn" :class="view === 'calendar' ? 'btn-primary' : 'btn-light'" @click="view = 'calendar'">
            <i class="fe fe-calendar mr-1"></i>Calendar
          </button>
        </div>
        <div v-if="view === 'calendar'" class="card appt-card"><AppointmentsCalendar /></div>
      </div>

      <!-- Appointments Table -->
      <div class="container m-t-20" v-show="view === 'list'">
        <div class="card appt-card">
          <div class="card-header-custom">
            <h5><i class="mdi mdi-format-list-bulleted mr-2"></i>All Appointments</h5>
          </div>
          <div class="card-body p-0">
            <div class="table-responsive">
              <table class="table appt-table mb-0">
                <thead>
                  <tr>
                    <th>Patient</th>
                    <th>Date</th>
                    <th>Time</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="!appointments.length">
                    <td colspan="6" class="text-center empty-state">
                      <i class="mdi mdi-calendar-blank" style="font-size:2rem;opacity:0.3"></i>
                      <p class="mt-2 mb-0">No appointments yet</p>
                    </td>
                  </tr>
                  <tr v-for="appoint in appointments" :key="appoint.id" class="appt-row">
                    <td class="patient-cell">
                      <div class="patient-avatar">{{ initials(appoint) }}</div>
                      <span>{{ appoint.patient ? appoint.patient.firstname + ' ' + appoint.patient.lastname : '—' }}</span>
                    </td>
                    <td>{{ appoint.appointDate | moment("MMM Do YYYY") }}</td>
                    <td>{{ appoint.appoinTime }}</td>
                    <td>
                      <span v-if="appoint.is_telemedicine" class="type-badge telemedicine">
                        <i class="fe fe-video"></i> telemedicine
                      </span>
                      <span v-else :class="appoint.type === 'virtual' ? 'type-badge virtual' : 'type-badge physical'">
                        <i :class="appoint.type === 'virtual' ? 'fe fe-video' : 'fe fe-map-pin'"></i>
                        {{ appoint.type || 'physical' }}
                      </span>
                    </td>
                    <td>
                      <span :class="'status-badge ' + (appoint.status || 'pending')">
                        {{ appoint.status || 'pending' }}
                      </span>
                    </td>
                    <td>
                      <router-link :to="{ path: '/appointment/' + appoint.id }">
                        <button class="btn btn-sm btn-view">
                          <i class="fe fe-eye mr-1"></i>View
                        </button>
                      </router-link>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <div class="vld-parent">
        <loading :active.sync="isLoading" loader="dots" :can-cancel="true" :is-full-page="fullPage" />
      </div>

      <!-- Create Appointment Modal -->
      <div class="modal fade" id="createModal" tabindex="-1" role="dialog" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered modal-lg" role="document">
          <div class="modal-content modal-styled">

            <div class="modal-header-styled">
              <div>
                <h5 class="modal-heading">New Appointment</h5>
                <p class="modal-sub">Fill in the details below to schedule an appointment</p>
              </div>
              <button type="button" class="modal-close-btn" data-dismiss="modal">
                <i class="fe fe-x"></i>
              </button>
            </div>

            <div class="modal-body px-4 pb-4">

              <!-- Step 1: Patient -->
              <div class="form-section">
                <div class="form-section-title">
                  <span class="section-number">1</span> Patient
                </div>
                <div class="form-row">
                  <div class="form-group col-md-6">
                    <label class="form-label">Search Patient</label>
                    <div class="search-input-wrap">
                      <i class="fe fe-search search-icon"></i>
                      <input
                        type="text"
                        class="form-control search-input"
                        placeholder="Name, ID, or phone..."
                        v-model="searchkey"
                        @input="searchPatient"
                      />
                      <span class="search-spinner" v-if="searching">
                        <i class="mdi mdi-loading mdi-spin"></i>
                      </span>
                    </div>
                  </div>
                  <div class="form-group col-md-6">
                    <label class="form-label">
                      Select Patient
                      <span class="found-badge" v-if="search_result.length">{{ search_result.length }} found</span>
                    </label>
                    <select class="form-control styled-select" v-model="selected_enrollee">
                      <option value="">-- Select from results --</option>
                      <option :value="p" v-for="p in search_result" :key="p.id">
                        {{ p.user_query }}
                      </option>
                    </select>
                  </div>
                </div>

                <div class="patient-preview" v-if="selected_enrollee && selected_enrollee.full_name">
                  <i class="fe fe-user patient-preview-icon"></i>
                  <div>
                    <strong>{{ selected_enrollee.full_name }}</strong>
                    <span class="ml-3 text-muted">DOB: {{ selected_enrollee.dob }}</span>
                    <span class="ml-3 text-muted" v-if="selected_enrollee.sector">| {{ selected_enrollee.sector }}</span>
                  </div>
                </div>
              </div>

              <!-- Step 2: Professional & Type -->
              <div class="form-section">
                <div class="form-section-title">
                  <span class="section-number">2</span> Details
                </div>
                <div class="form-row">
                  <div class="form-group col-md-6">
                    <label class="form-label">Attending Professional</label>
                    <select class="form-control styled-select" v-model="appointment.professional_id">
                      <option value="">-- Select Professional --</option>
                      <option :value="emp.id" v-for="emp in employees" :key="emp.id">
                        {{ emp.firstname }} {{ emp.lastname }}{{ empSpecs(emp.id) }}
                      </option>
                    </select>
                    <small class="text-muted" v-if="!employees.length">No professionals found for this facility.</small>
                  </div>
                  <div class="form-group col-md-6">
                    <label class="form-label">Appointment Type</label>
                    <div class="type-toggle">
                      <button
                        type="button"
                        :class="['type-opt', appointment.type === 'physical' ? 'active' : '']"
                        @click="appointment.type = 'physical'"
                      >
                        <i class="fe fe-map-pin mr-1"></i> Physical
                      </button>
                      <button
                        type="button"
                        :class="['type-opt', appointment.type === 'virtual' ? 'active virtual-active' : '']"
                        @click="appointment.type = 'virtual'"
                      >
                        <i class="fe fe-video mr-1"></i> Virtual
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Step 3: Date & Time -->
              <div class="form-section">
                <div class="form-section-title">
                  <span class="section-number">3</span> Schedule
                </div>
                <div class="form-row">
                  <div class="form-group col-md-6">
                    <label class="form-label">Appointment Date</label>
                    <input type="date" class="form-control styled-input" v-model="appointment.appointDate" />
                  </div>
                  <div class="form-group col-md-6">
                    <label class="form-label">Appointment Time</label>
                    <input type="time" class="form-control styled-input" v-model="appointment.appoinTime" />
                  </div>
                </div>
              </div>

            </div>

            <div class="modal-footer-styled">
              <button type="button" class="btn btn-cancel" data-dismiss="modal">Cancel</button>
              <button class="btn btn-create" @click="createAppointment" :disabled="isLoading">
                <span v-if="isLoading"><i class="mdi mdi-loading mdi-spin mr-1"></i>Creating...</span>
                <span v-else><i class="fe fe-check mr-1"></i>Create Appointment</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <SendTelemedicine :open="teleOpen" @close="teleOpen = false" @sent="$router.push('/telemedicine')" />

    </main>
  </section>
</template>

<script>
import Navbar from "@/views/Navbar.vue";
import SendTelemedicine from "@/views/telemedicine/SendTelemedicine.vue";
import AppointmentsCalendar from "./AppointmentsCalendar.vue";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";

let agency = JSON.parse(localStorage.getItem("agency"));

function parseList(data) {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.data)) return data.data;
  return [];
}

let searchTimer = null;

export default {
  components: { Navbar, Loading, SendTelemedicine, AppointmentsCalendar },
  data() {
    return {
      isLoading: false,
      teleOpen: false,
      view: "list",
      searching: false,
      fullPage: true,
      user: null,
      agency: agency,
      appointments: [],
      employees: [],
      specializations: {},
      searchkey: "",
      search_result: [],
      selected_enrollee: "",
      appointment: {
        professional_id: "",
        appoinTime: "",
        appointDate: "",
        type: "physical",
      },
    };
  },
  computed: {
    stats() {
      const counts = { pending: 0, accepted: 0, completed: 0, declined: 0 };
      this.appointments.forEach((a) => {
        const s = a.status || "pending";
        if (counts[s] !== undefined) counts[s]++;
      });
      return [
        { label: "Pending",   count: counts.pending,   color: "#f59e0b" },
        { label: "Accepted",  count: counts.accepted,  color: "#3b82f6" },
        { label: "Completed", count: counts.completed, color: "#10b981" },
        { label: "Declined",  count: counts.declined,  color: "#ef4444" },
      ];
    },
    providerId() {
      if (!this.user) return null;
      return this.user.type === "provider" ? this.user.id : this.user.institutional_id;
    },
  },
  methods: {
    // Specializations of the facility's professionals, shown in the dropdown: " — Paediatrics, Cardiology".
    loadSpecializations() {
      const ids = this.employees.map((e) => e.id);
      if (!ids.length) return;
      this.axios
        .get("/api/v1/auth/specializations/users", { params: { ids } })
        .then((res) => { this.specializations = res.data.data || {}; })
        .catch(() => {});
    },
    empSpecs(id) {
      const list = this.specializations[id] || [];
      return list.length ? " — " + list.map((s) => s.name).join(", ") : "";
    },
    initials(appoint) {
      if (!appoint.patient) return "?";
      return ((appoint.patient.firstname || "")[0] + (appoint.patient.lastname || "")[0]).toUpperCase();
    },
    searchPatient() {
      clearTimeout(searchTimer);
      if (!this.searchkey.trim()) { this.search_result = []; return; }
      searchTimer = setTimeout(() => {
        this.searching = true;
        this.axios
          .post("/api/v1/auth/searchenrollees", {
            agency_id: this.agency.id,
            request_query: this.searchkey,
          })
          .then((res) => {
            this.search_result = parseList(res.data);
            this.searching = false;
          })
          .catch((err) => { console.error(err); this.searching = false; });
      }, 400);
    },
    getAppointments() {
      this.user = JSON.parse(localStorage.getItem("user"));
      this.axios
        .get(`/api/v1/auth/appointments`,{
            params: { provider_id: this.providerId }
        })
        .then((res) => { this.appointments = parseList(res.data); })
        .catch(console.error);
    },
    getEmployees() {
      this.user = JSON.parse(localStorage.getItem("user"));
      this.axios
        .get(`/api/v1/auth/getEmployee/${this.providerId}`)
        .then((res) => {
          this.employees = parseList(res.data);
          this.loadSpecializations();
        })
        .catch(console.error);
    },
    createAppointment() {
      if (!this.selected_enrollee || !this.selected_enrollee.id) {
        this.$breadstick.notify("Please search and select a patient.", { position: "top-right" });
        return;
      }
      if (!this.appointment.appointDate || !this.appointment.appoinTime) {
        this.$breadstick.notify("Please set the date and time.", { position: "top-right" });
        return;
      }
      this.isLoading = true;
      this.axios
        .post("/api/v1/auth/appointments", {
          patient_id: this.selected_enrollee.type === "client" ? this.selected_enrollee.id : 0,
          dependent_id: this.selected_enrollee.type === "Dependent" ? this.selected_enrollee.id : null,
          professional_id: this.appointment.professional_id,
          provider_id: this.providerId,
          user_id: this.user.id,
          appoinTime: this.appointment.appoinTime,
          appointDate: this.appointment.appointDate,
          type: this.appointment.type,
          status: "pending",
        })
        .then(() => {
          this.isLoading = false;
          this.getAppointments();
          this.resetForm();
          this.$breadstick.notify("Appointment created successfully", { position: "top-right" });
          window.$('#createModal').modal('hide');
        })
        .catch((err) => {
          console.error(err);
          this.isLoading = false;
          this.$breadstick.notify("Something went wrong. Please try again.", { position: "top-right" });
        });
    },
    resetForm() {
      this.searchkey = "";
      this.search_result = [];
      this.selected_enrollee = "";
      this.appointment = { professional_id: "", appoinTime: "", appointDate: "", type: "physical" };
    },
  },
  beforeMount() {
    this.user = JSON.parse(localStorage.getItem("user"));
  },
  created() {
    this.getAppointments();
    this.getEmployees();
  },
};
</script>

<style scoped>
/* ── Page Header ── */
.page-header {
  background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%);
  color: #fff;
}
.page-title { font-size: 1.6rem; font-weight: 700; margin: 0; color: #fff; }
.page-subtitle { opacity: .75; margin: 4px 0 0; font-size: .9rem; }
.m-t-neg { margin-top: -28px; }

/* ── Stat Cards ── */
.stat-card {
  background: #fff;
  border-radius: 10px;
  border-left: 4px solid;
  padding: 16px 20px;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,.08);
}
.stat-count { font-size: 2rem; font-weight: 700; margin: 0; }
.stat-label { font-size: .8rem; color: #6b7280; margin: 0; text-transform: uppercase; letter-spacing: .05em; }

/* ── Appointments Table Card ── */
.appt-card {
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0,0,0,.08);
  border: none;
  margin-bottom: 30px;
}
.card-header-custom {
  padding: 16px 24px;
  border-bottom: 1px solid #f1f5f9;
  background: #fff;
  border-radius: 12px 12px 0 0;
}
.card-header-custom h5 { margin: 0; font-weight: 600; color: #1e3a5f; }

.appt-table { font-size: .9rem; }
.appt-table thead th {
  background: #f8fafc;
  color: #64748b;
  font-weight: 600;
  font-size: .78rem;
  text-transform: uppercase;
  letter-spacing: .06em;
  border-bottom: 2px solid #e2e8f0;
  padding: 14px 20px;
}
.appt-table tbody td { padding: 14px 20px; vertical-align: middle; border-color: #f1f5f9; }
.appt-row:hover { background: #f8fafc; }

/* Patient cell */
.patient-cell { display: flex; align-items: center; gap: 10px; }
.patient-avatar {
  width: 34px; height: 34px; border-radius: 50%;
  background: linear-gradient(135deg, #2563eb, #7c3aed);
  color: #fff; font-size: .75rem; font-weight: 700;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}

/* Type badge */
.type-badge {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 4px 10px; border-radius: 20px; font-size: .78rem; font-weight: 600;
}
.type-badge.physical { background: #f1f5f9; color: #475569; }
.type-badge.virtual  { background: #dbeafe; color: #1d4ed8; }
.type-badge.telemedicine { background: #ede9fe; color: #5b21b6; }
.btn-tele { background: #fff; color: #5b21b6; border: none; border-radius: 8px; padding: 10px 16px; font-weight: 600; }
.btn-tele:hover { opacity: .9; color: #5b21b6; }

/* Status badge */
.status-badge {
  display: inline-block; padding: 4px 12px; border-radius: 20px;
  font-size: .78rem; font-weight: 600; text-transform: capitalize;
}
.status-badge.pending   { background: #fef3c7; color: #92400e; }
.status-badge.accepted  { background: #dbeafe; color: #1e40af; }
.status-badge.completed { background: #d1fae5; color: #065f46; }
.status-badge.declined  { background: #fee2e2; color: #991b1b; }

/* Buttons */
.btn-create {
  background: linear-gradient(135deg, #2563eb, #7c3aed);
  color: #fff; border: none; border-radius: 8px; padding: 10px 20px; font-weight: 600;
}
.btn-create:hover { opacity: .9; color: #fff; }
.btn-view { border-radius: 6px; border: 1px solid #e2e8f0; color: #1e3a5f; background: #fff; font-size:.8rem; }
.btn-view:hover { background: #f1f5f9; }
.btn-cancel { background: #f1f5f9; border: none; border-radius: 8px; padding: 10px 20px; color: #475569; font-weight: 600; }

/* Empty state */
.empty-state { padding: 40px !important; color: #94a3b8; }

/* ── Modal ── */
.modal-styled { border-radius: 16px; overflow: hidden; border: none; }
.modal-header-styled {
  display: flex; justify-content: space-between; align-items: flex-start;
  padding: 24px 28px 16px;
  background: linear-gradient(135deg, #1e3a5f, #2563eb);
  color: #fff;
}
.modal-heading { font-size: 1.2rem; font-weight: 700; margin: 0; color: #fff; }
.modal-sub { font-size: .82rem; opacity: .75; margin: 4px 0 0; }
.modal-close-btn { background: rgba(255,255,255,.15); border: none; border-radius: 8px; color: #fff; width: 32px; height: 32px; cursor: pointer; }
.modal-close-btn:hover { background: rgba(255,255,255,.25); }
.modal-footer-styled {
  display: flex; justify-content: flex-end; gap: 10px;
  padding: 16px 24px; border-top: 1px solid #f1f5f9; background: #f8fafc;
}

/* Form sections */
.form-section { margin-bottom: 20px; padding-bottom: 20px; border-bottom: 1px solid #f1f5f9; }
.form-section:last-child { border-bottom: none; margin-bottom: 0; padding-bottom: 0; }
.form-section-title {
  display: flex; align-items: center; gap: 10px;
  font-weight: 600; color: #374151; font-size: .9rem; margin-bottom: 14px;
}
.section-number {
  width: 24px; height: 24px; border-radius: 50%;
  background: #2563eb; color: #fff;
  font-size: .75rem; font-weight: 700;
  display: inline-flex; align-items: center; justify-content: center;
}

/* Patient search */
.search-input-wrap { position: relative; }
.search-icon { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: #94a3b8; }
.search-input { padding-left: 36px; }
.search-spinner { position: absolute; right: 12px; top: 50%; transform: translateY(-50%); color: #94a3b8; }
.found-badge {
  display: inline-block; background: #dbeafe; color: #1d4ed8;
  font-size: .7rem; font-weight: 700; padding: 1px 7px; border-radius: 20px; margin-left: 6px;
}

.patient-preview {
  display: flex; align-items: center; gap: 12px;
  background: #f0fdf4; border: 1px solid #bbf7d0;
  border-radius: 8px; padding: 10px 14px; margin-top: 4px; font-size: .88rem;
}
.patient-preview-icon { font-size: 1.2rem; color: #10b981; }

/* Type toggle */
.type-toggle { display: flex; gap: 8px; }
.type-opt {
  flex: 1; padding: 9px; border: 2px solid #e2e8f0;
  border-radius: 8px; background: #fff; color: #64748b;
  font-size: .85rem; font-weight: 600; cursor: pointer; transition: all .2s;
}
.type-opt.active { border-color: #2563eb; background: #eff6ff; color: #1d4ed8; }
.type-opt.virtual-active { border-color: #7c3aed; background: #f5f3ff; color: #5b21b6; }

/* Inputs */
.form-label { font-weight: 600; font-size: .85rem; color: #374151; margin-bottom: 6px; }
.styled-select, .styled-input {
  border: 1.5px solid #e2e8f0; border-radius: 8px;
  padding: 9px 12px; font-size: .88rem;
  transition: border-color .2s;
}
.styled-select:focus, .styled-input:focus { border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37,99,235,.1); outline: none; }

.m-t-20 { margin-top: 20px; }
</style>
